/*
 * Shared Terms components for MySchoolRx product.
 *
 * <TermsAcceptanceGate /> — full-screen overlay that blocks the wrapped child
 *   tree until the logged-in user accepts the current active Terms version.
 *   Used inside the diagnostic workspace (first-login gate + new-version gate).
 *
 * <TermsCheckbox /> — checkout-time inline checkbox + link to a Terms modal.
 *   Used in CaporilliSignup before the Submit Request button.
 *
 * <TermsViewerModal /> — read-only modal that fetches `/api/terms/active/...`
 *   and renders the Markdown-lite body. Reused by both surfaces.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { ShieldCheck, X, CheckCircle2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const getDiagHdrs = () => {
  try {
    const s = JSON.parse(localStorage.getItem("diag_session") || "null");
    if (!s?.user?.id || !s?.tenant?.id) return null;
    return { "x-tenant-id": s.tenant.id, "x-user-id": s.user.id, "x-user-role": s.user.role || "tenant_admin" };
  } catch { return null; }
};

// ────────────────────────────────────────────────────────────────────────────
// Markdown-lite renderer (matches the admin preview so behaviour is identical)
// ────────────────────────────────────────────────────────────────────────────
const MarkdownLite = ({ text }) => {
  const lines = (text || "").split("\n");
  const out = [];
  let ulBuf = [];
  const flushUl = () => {
    if (ulBuf.length) {
      out.push(
        <ul key={`ul-${out.length}`} className="list-disc pl-6 my-2 space-y-1">
          {ulBuf.map((b, i) => (
            <li key={i} className="text-[13px] text-[#374151] leading-relaxed" dangerouslySetInnerHTML={{ __html: inline(b) }} />
          ))}
        </ul>
      );
      ulBuf = [];
    }
  };
  const inline = (s) => s
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>");
  lines.forEach((line, i) => {
    if (line.startsWith("# ")) { flushUl(); out.push(<h1 key={i} className="text-2xl font-bold text-[#111827] mt-4 mb-2">{line.slice(2)}</h1>); }
    else if (line.startsWith("## ")) { flushUl(); out.push(<h2 key={i} className="text-lg font-bold text-[#111827] mt-4 mb-2">{line.slice(3)}</h2>); }
    else if (line.startsWith("### ")) { flushUl(); out.push(<h3 key={i} className="text-base font-bold text-[#111827] mt-3 mb-1">{line.slice(4)}</h3>); }
    else if (line.startsWith("- ")) { ulBuf.push(line.slice(2)); }
    else if (line.trim() === "") { flushUl(); out.push(<div key={i} className="h-2" />); }
    else { flushUl(); out.push(<p key={i} className="text-[13px] text-[#374151] leading-relaxed" dangerouslySetInnerHTML={{ __html: inline(line) }} />); }
  });
  flushUl();
  return <div>{out}</div>;
};

// ────────────────────────────────────────────────────────────────────────────
// Read-only Terms viewer modal
// ────────────────────────────────────────────────────────────────────────────
export const TermsViewerModal = ({ productType = "caporilli_diagnostic", onClose, terms: termsProp = null }) => {
  const [terms, setTerms] = useState(termsProp);
  const [loading, setLoading] = useState(!termsProp);

  useEffect(() => {
    if (termsProp) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/terms/active/${productType}`);
        setTerms(r.data);
      } catch {
        toast.error("Could not load Terms & Conditions");
      } finally {
        setLoading(false);
      }
    })();
  }, [productType, termsProp]);

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="terms-viewer-modal">
        <div className="px-6 py-4 border-b border-[#e5e7eb] flex items-center justify-between sticky top-0 bg-white">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wider text-[#6b7280] font-semibold">Terms & Conditions</div>
            <h2 className="text-lg font-bold text-[#111827] truncate">
              {terms?.title || "Loading…"}{terms?.version ? ` · v${terms.version}` : ""}
            </h2>
            {terms?.effective_date && (
              <div className="text-[11px] text-[#6b7280] mt-0.5">Effective {new Date(terms.effective_date).toLocaleDateString()}</div>
            )}
          </div>
          <button onClick={onClose} className="text-[#6b7280] hover:text-[#111827]" data-testid="terms-viewer-close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="px-6 py-5 overflow-y-auto flex-1">
          {loading ? (
            <div className="text-center text-[#9ca3af] py-10">Loading Terms…</div>
          ) : (
            <MarkdownLite text={terms?.body || ""} />
          )}
        </div>
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Checkout inline checkbox — fetches the active Terms version and exposes
// `acceptedTermsId` + `acceptedTermsVersion` via onChange.
// ────────────────────────────────────────────────────────────────────────────
export const TermsCheckbox = ({ productType = "caporilli_diagnostic", value, onChange }) => {
  const [terms, setTerms] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/terms/active/${productType}`);
        setTerms(r.data);
      } catch {
        // Fail-open is unsafe — show error inline
        setTerms({ error: true });
      }
    })();
  }, [productType]);

  if (!terms) {
    return <div className="text-[11px] text-[#9ca3af]">Loading Terms…</div>;
  }
  if (terms.error) {
    return <div className="text-[11px] text-[#b91c1c]">Terms could not be loaded. Please refresh.</div>;
  }

  const checked = !!value && value.terms_document_id === terms.id;

  return (
    <>
      <label className="flex items-start gap-3 p-3 rounded-md border border-[#e5e7eb] bg-[#f9fafb] cursor-pointer" data-testid="terms-checkbox-label">
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4 text-[#00698a] border-[#cbd5e1] rounded focus:ring-[#007a9e]"
          checked={checked}
          onChange={(e) => onChange(e.target.checked ? { terms_document_id: terms.id, terms_version: terms.version } : null)}
          data-testid="terms-checkbox-input"
        />
        <span className="text-[12px] text-[#374151] leading-relaxed flex-1">
          I have read and agree to the{" "}
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); setShowModal(true); }}
            className="text-[#1a56db] hover:text-[#1e40af] underline font-semibold"
            data-testid="terms-checkbox-open-modal"
          >
            MySchoolRx Terms &amp; Conditions
          </button>
          {" "}(v{terms.version}, effective {new Date(terms.effective_date).toLocaleDateString()}).
        </span>
      </label>
      {showModal && <TermsViewerModal productType={productType} onClose={() => setShowModal(false)} terms={terms} />}
    </>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// First-login gate — blocks children until user accepts current active Terms.
// Used inside the Diagnostic workspace shell.
// ────────────────────────────────────────────────────────────────────────────
export const TermsAcceptanceGate = ({ productType = "caporilli_diagnostic", children }) => {
  const [state, setState] = useState({ loading: true, needs: false, terms: null });
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const checkAcceptance = async () => {
    const h = getDiagHdrs();
    // Portfolio "Open School" context: the portfolio user keeps their own
    // identity and should not be forced to accept the school's terms.
    try {
      const s = JSON.parse(localStorage.getItem("diag_session") || "null");
      if (s?.acting_as === "portfolio" || s?.act_as) {
        setState({ loading: false, needs: false, terms: null });
        return;
      }
    } catch { /* ignore */ }
    if (!h) {
      setState({ loading: false, needs: false, terms: null });
      return;
    }
    try {
      const r = await axios.get(`${API}/api/terms/needs-acceptance/${productType}`, { headers: h });
      setState({ loading: false, needs: !!r.data.needs_acceptance, terms: r.data.active_terms });
    } catch {
      // If endpoint fails (e.g., session missing), unblock so we don't trap the user
      setState({ loading: false, needs: false, terms: null });
    }
  };

  useEffect(() => { checkAcceptance(); /* eslint-disable-next-line */ }, [productType]);

  const accept = async () => {
    if (!accepted || !state.terms) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/api/terms/accept`,
        { product_type: productType, terms_document_id: state.terms.id, acceptance_context: "first_login" },
        { headers: getDiagHdrs() },
      );
      toast.success("Terms accepted");
      setState((s) => ({ ...s, needs: false }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to record acceptance");
    } finally {
      setSubmitting(false);
    }
  };

  if (state.loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#f9fafb] text-[#6b7280]">Loading…</div>;
  }
  if (!state.needs) {
    return children;
  }

  return (
    <div className="min-h-screen bg-[#f9fafb] flex items-center justify-center p-4" data-testid="terms-gate">
      <div className="max-w-3xl w-full bg-white rounded-2xl border border-[#e5e7eb] shadow-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-[#e5e7eb] flex items-center justify-between bg-[#0b1220] text-white">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-[#00698a]" />
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[#00698a] font-semibold">Action required</div>
              <h1 className="text-lg font-bold">Accept the Terms & Conditions to continue</h1>
            </div>
          </div>
          <div className="text-[11px] text-white/60 hidden sm:block">v{state.terms?.version}</div>
        </div>
        <div className="px-6 py-5 max-h-[55vh] overflow-y-auto border-b border-[#e5e7eb]">
          <MarkdownLite text={state.terms?.body || ""} />
        </div>
        <div className="px-6 py-4 space-y-3">
          <label className="flex items-start gap-3 cursor-pointer" data-testid="terms-gate-checkbox-label">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 text-[#00698a] border-[#cbd5e1] rounded"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              data-testid="terms-gate-checkbox"
            />
            <span className="text-[13px] text-[#374151]">
              I have read and agree to MySchoolRx Terms & Conditions (v{state.terms?.version}, effective {state.terms ? new Date(state.terms.effective_date).toLocaleDateString() : "—"}).
            </span>
          </label>
          <div className="flex items-center justify-end gap-3">
            <Button
              onClick={accept}
              disabled={!accepted || submitting}
              className="bg-[#007a9e] hover:bg-[#00607d] text-white disabled:opacity-50"
              data-testid="terms-gate-accept-btn"
            >
              {submitting ? "Recording…" : (
                <><CheckCircle2 className="h-4 w-4 mr-1.5" /> I accept &amp; continue</>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
